import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { PageShellComponent, TabStripComponent, TabStripItem } from '@clinic-platform/ui-components';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'app-notifications-tabs-page',
  standalone: true,
  imports: [PageShellComponent, TabStripComponent, RouterOutlet],
  template: `
    <lib-page-shell>
      <lib-tab-strip
        [items]="tabs"
        [activeId]="activeTab"
        (activeIdChange)="onTabChange($event)"
        ariaLabel="Notification tabs"
      />

      <router-outlet />
    </lib-page-shell>
  `,
})
export class NotificationsTabsPageComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly subscriptions = new Subscription();

  protected tabs: TabStripItem[] = [];
  protected activeTab: string = 'queue';

  public ngOnInit(): void {
    this.rebuildTabs();
    this.updateActiveTabByUrl(this.router.url);
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.updateActiveTabByUrl((event as NavigationEnd).urlAfterRedirects);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected onTabChange(tabId: string): void {
    if (!this.tabs.some((tab) => tab.id === tabId)) {
      return;
    }

    void this.router.navigate([tabId], { relativeTo: this.route });
  }

  private rebuildTabs(): void {
    const role = this.authService.getCurrentRole();
    const canAccessTemplates = role === 'system_admin' || role === 'company_admin' || role === 'manager';
    const tabs: TabStripItem[] = [{ id: 'queue', label: 'Queue' }];

    if (canAccessTemplates) {
      tabs.push({ id: 'templates', label: 'Templates' });
    }

    this.tabs = tabs;
  }

  private updateActiveTabByUrl(url: string): void {
    const clean = (url ?? '').split('?')[0];
    const targetTab = clean.includes('/notifications/templates') ? 'templates' : 'queue';

    this.activeTab = this.tabs.some((tab) => tab.id === targetTab) ? targetTab : 'queue';
  }
}
